/**
 * ReportDetailPage — Tela de leitura do relatorio narrativo individual (/reports/:id).
 *
 * Estados implementados (wireframe §1.7):
 *   loading         : skeleton (abertura + mudancas + analise)
 *   ready           : relatorio completo 5 blocos
 *   ready-partial   : relatorio com nota inline de fonte com gap no Bloco 5
 *   ready-no-actions : Bloco 4 ausente — mensagem "nenhuma acao urgente"
 *   ia-unavailable  : Blocos 1, 2, 5 presentes; Blocos 3, 4 com nota + botao retry
 *   not-found       : ID invalido ou removido
 *   error           : falha ao buscar
 *
 * Regras:
 *   - Numeros com [ref:] do wireframe (anotacao interna no texto da API)
 *   - Todo numero exibido tem fonte — nunca silencio
 *   - Titulo do relatorio sticky no scroll
 *   - Chip "Perguntar ao CROSS" navega para /chat?context=report:[id]:bloco3
 *   - Cards de acao com border-left por urgencia (now/this_week/next_month)
 *   - Botao "Gerar agora" chama POST /reports/generate (D-045: geracao manual)
 *   - Zero string hardcoded fora de i18n
 *   - A11y AA
 */

import '../styles/screen-reports.css';

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { Skeleton } from '../components/Skeleton';
import { apiClient, ApiError } from '../lib/api-client';

/* ================================================================
   TIPOS — contrato de API (wireframe §HANDOFF)
   ================================================================ */

interface SourceRef {
  readonly id: string;
  readonly source: string;
  readonly account?: string;
  readonly period?: string;
  readonly collected_at?: string;
}

interface ChangeItem {
  readonly metric_key: string;
  readonly label: string;
  readonly value: string;
  readonly delta: string | null;
  readonly delta_label: string | null;
  readonly refs: readonly SourceRef[];
}

interface ActionItem {
  readonly title: string;
  readonly description: string;
  readonly urgency: 'now' | 'this_week' | 'next_month';
  readonly link?: { readonly label: string; readonly href: string };
  readonly refs: readonly SourceRef[];
}

interface ReportSource {
  readonly source: string;
  readonly account: string;
  readonly period_from: string;
  readonly period_to: string;
  readonly collected_at: string;
  readonly gaps?: readonly string[];
}

interface ReportDetail {
  readonly id: string;
  readonly type: 'weekly' | 'monthly';
  readonly period_from: string;
  readonly period_to: string;
  readonly period_label?: string;
  readonly generated_at: string;
  readonly ai_status: 'ready' | 'fallback' | 'pending';
  readonly blocks: {
    readonly opening: { readonly text: string; readonly refs: readonly SourceRef[] };
    readonly changes: { readonly items: readonly ChangeItem[] };
    readonly analysis: { readonly text: string | null; readonly refs: readonly SourceRef[] };
    readonly actions: readonly ActionItem[] | null;
    readonly sources: readonly ReportSource[];
  };
}

interface GenerateResponse {
  readonly status: 'generated' | 'skipped' | 'pending';
  readonly report_id?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: ReportDetail }
  | { kind: 'not-found' }
  | { kind: 'error' };

type IaRetryState = { kind: 'idle' } | { kind: 'generating' } | { kind: 'error'; message: string };

/* ================================================================
   HELPERS
   ================================================================ */

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function sourceLabel(source: string): string {
  const labels: Record<string, string> = {
    instagram: 'Instagram',
    meta_ads: 'Meta Ads',
    google_drive: 'Planilhas Drive',
  };
  return labels[source] ?? source;
}

/** Determina o icone de urgencia por nível. */
function urgencyIcon(urgency: ActionItem['urgency']): string {
  if (urgency === 'now') return 'priority_high';
  if (urgency === 'this_week') return 'schedule';
  return 'event';
}

/** Determina o label de urgencia. */
function urgencyLabel(urgency: ActionItem['urgency']): string {
  if (urgency === 'now') return t('report.urgencyNow');
  if (urgency === 'this_week') return t('report.urgencyThisWeek');
  return t('report.urgencyNextMonth');
}

/* ================================================================
   BLOCO 1 — ABERTURA
   ================================================================ */

interface Block1Props {
  readonly opening: ReportDetail['blocks']['opening'];
  readonly isMonthly: boolean;
}

function Block1Opening({ opening, isMonthly }: Block1Props): JSX.Element {
  const heading = isMonthly ? t('report.sectionOpeningMonthly') : t('report.sectionOpening');

  return (
    <section className="report-block" aria-labelledby="report-bloco1-heading">
      <h2 id="report-bloco1-heading" className="report-block-heading">
        <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
          radio_button_checked
        </span>
        {heading}
      </h2>
      <hr className="report-block-divider" aria-hidden="true" />

      <p className="report-block-opening-text">{opening.text}</p>

      {opening.refs.length > 0 ? (
        <div className="report-block-opening-refs" aria-label="Fontes da abertura">
          {opening.refs.map((ref) => (
            <span key={ref.id} className="kb-chip" title={ref.source}>
              <span
                className="material-symbols-rounded"
                aria-hidden="true"
                style={{ fontSize: 12 }}
              >
                link
              </span>
              {ref.source}
              {ref.period ? ` · ${ref.period}` : ''}
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/* ================================================================
   BLOCO 2 — O QUE MUDOU
   ================================================================ */

interface Block2Props {
  readonly changes: ReportDetail['blocks']['changes'];
}

function Block2Changes({ changes }: Block2Props): JSX.Element {
  return (
    <section className="report-block" aria-labelledby="report-bloco2-heading">
      <h2 id="report-bloco2-heading" className="report-block-heading">
        <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
          swap_vert
        </span>
        {t('report.sectionChanges')}
      </h2>
      <hr className="report-block-divider" aria-hidden="true" />

      <ul className="report-changes-list" aria-label={t('report.sectionChanges')}>
        {changes.items.map((item, idx) => {
          // Determina sinal do item
          let signChar = '=';
          let signClass = 'neutral';
          if (item.delta !== null) {
            const num = parseFloat(item.delta.replace(/[^0-9.-]/g, ''));
            if (num > 0) {
              signChar = '+';
              signClass = 'positive';
            } else if (num < 0) {
              signChar = '−';
              signClass = 'negative';
            }
          }

          return (
            <li key={`change-${idx}`} className="report-change-item">
              <span
                className={`report-change-sign ${signClass}`}
                aria-label={
                  signClass === 'positive'
                    ? 'Aumento'
                    : signClass === 'negative'
                      ? 'Queda'
                      : 'Estavel'
                }
              >
                {signChar}
              </span>
              <div className="report-change-content">
                <p className="report-change-label">{item.label}</p>
                <span className="report-change-value">
                  {item.value.length > 0 ? (
                    item.value
                  ) : (
                    <span className="report-change-no-data">{t('report.changesNoData')}</span>
                  )}
                </span>
                {item.delta !== null && item.delta_label !== null ? (
                  <p className="report-change-delta">
                    {item.delta} {item.delta_label}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      <Link to="/dashboard" className="report-changes-view-all">
        {t('report.viewDashboard')}
        <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 14 }}>
          arrow_forward
        </span>
      </Link>
    </section>
  );
}

/* ================================================================
   BLOCO 3 — POR QUE IMPORTA
   ================================================================ */

interface Block3Props {
  readonly analysis: ReportDetail['blocks']['analysis'];
  readonly reportId: string;
}

function Block3Analysis({ analysis, reportId }: Block3Props): JSX.Element {
  const chatUrl = `/chat?context=report:${reportId}:bloco3`;

  // Divide o texto em paragrafos (separados por \n\n ou \n)
  const paragraphs =
    analysis.text !== null ? analysis.text.split(/\n\n|\n/).filter((p) => p.trim().length > 0) : [];

  return (
    <section className="report-block report-block-fade-in" aria-labelledby="report-bloco3-heading">
      <h2 id="report-bloco3-heading" className="report-block-heading">
        <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
          psychology
        </span>
        {t('report.sectionAttention')}
      </h2>
      <hr className="report-block-divider" aria-hidden="true" />

      <div className="report-analysis-text">
        {paragraphs.map((para, idx) => (
          <p key={idx}>{para}</p>
        ))}
      </div>

      <Link
        to={chatUrl}
        className="report-ask-cross-chip"
        aria-label={t('report.askCrossAboutAnalysis')}
      >
        <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
          forum
        </span>
        {t('report.askCrossAboutAnalysis')}
      </Link>
    </section>
  );
}

/* ================================================================
   BLOCO 4 — O QUE FAZER
   ================================================================ */

interface Block4Props {
  readonly actions: readonly ActionItem[] | null;
  readonly reportId: string;
}

function Block4Actions({ actions, reportId }: Block4Props): JSX.Element {
  return (
    <section className="report-block report-block-fade-in" aria-labelledby="report-bloco4-heading">
      <h2 id="report-bloco4-heading" className="report-block-heading">
        <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
          task_alt
        </span>
        {t('report.sectionActions')}
      </h2>
      <hr className="report-block-divider" aria-hidden="true" />

      {actions === null || actions.length === 0 ? (
        <p className="report-no-actions">{t('report.noActionsMessage')}</p>
      ) : (
        <ul className="report-actions-list" aria-label={t('report.sectionActions')}>
          {actions.map((action, idx) => (
            <li key={`action-${idx}`}>
              <article
                className="report-action-card"
                data-urgency={action.urgency}
                aria-label={action.title}
              >
                <div className="report-action-card-header">
                  <span className="report-action-urgency-label" data-urgency={action.urgency}>
                    <span
                      className="material-symbols-rounded"
                      aria-hidden="true"
                      style={{ fontSize: 16 }}
                    >
                      {urgencyIcon(action.urgency)}
                    </span>
                    {urgencyLabel(action.urgency)}
                  </span>
                </div>

                <h3 className="report-action-title">{action.title}</h3>
                <p className="report-action-description">{action.description}</p>

                <div className="report-action-links">
                  {action.link !== undefined ? (
                    <Link
                      to={action.link.href}
                      className="report-action-link"
                      aria-label={`${t('report.actionLinkView')} — ${action.link.label}`}
                    >
                      {action.link.label}
                      <span
                        className="material-symbols-rounded"
                        aria-hidden="true"
                        style={{ fontSize: 14 }}
                      >
                        arrow_forward
                      </span>
                    </Link>
                  ) : null}

                  <Link
                    to={`/chat?context=report:${reportId}:acao:${idx}`}
                    className="report-action-link"
                    aria-label={`${t('report.actionAskCross')} — ${action.title}`}
                  >
                    <span
                      className="material-symbols-rounded"
                      aria-hidden="true"
                      style={{ fontSize: 14 }}
                    >
                      forum
                    </span>
                    {t('report.actionAskCross')}
                  </Link>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ================================================================
   BLOCO 5 — FONTES
   ================================================================ */

interface Block5Props {
  readonly sources: readonly ReportSource[];
}

function Block5Sources({ sources }: Block5Props): JSX.Element {
  return (
    <section className="report-block" aria-labelledby="report-bloco5-heading">
      <h2 id="report-bloco5-heading" className="report-block-heading">
        <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
          dataset
        </span>
        {t('report.sectionSources')}
      </h2>
      <hr className="report-block-divider" aria-hidden="true" />

      <ul className="report-sources-list" aria-label={t('report.sectionSources')}>
        {sources.map((src, idx) => {
          const hasGaps = Array.isArray(src.gaps) && src.gaps.length > 0;
          const periodFrom = formatDate(src.period_from);
          const periodTo = formatDate(src.period_to);
          const collectedAt = formatDate(src.collected_at);

          return (
            <li key={`src-${idx}`} className="report-source-item">
              <span className={`report-source-dot ${src.source}`} aria-hidden="true" />
              <div className="report-source-info">
                <span className="report-source-name">
                  {sourceLabel(src.source)} · {src.account}
                </span>
                <p className="report-source-period">
                  {periodFrom}–{periodTo} · coletado {collectedAt}
                </p>
                {hasGaps ? (
                  <p className="report-source-gap-note" role="note">
                    {t('report.periodPartial', {
                      source: sourceLabel(src.source),
                      dates: (src.gaps ?? []).join(', '),
                    })}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      <Link to="/sources" className="report-sources-verify-link">
        <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 14 }}>
          hub
        </span>
        {t('report.verifySources')}
      </Link>
    </section>
  );
}

/* ================================================================
   BANNER IA INDISPONIVEL
   ================================================================ */

interface IaUnavailableBannerProps {
  readonly retryState: IaRetryState;
  readonly onRetry: () => void;
}

function IaUnavailableBanner({ retryState, onRetry }: IaUnavailableBannerProps): JSX.Element {
  const isGenerating = retryState.kind === 'generating';

  return (
    <div className="report-ia-unavailable-banner" role="status">
      <span className="material-symbols-rounded report-ia-unavailable-icon" aria-hidden="true">
        info
      </span>
      <div className="report-ia-unavailable-body">
        <p className="report-ia-unavailable-title">{t('report.iaUnavailableTitle')}</p>
        <p className="report-ia-unavailable-text">{t('report.iaUnavailableBody')}</p>

        <button
          type="button"
          className="report-ia-retry-btn"
          onClick={onRetry}
          disabled={isGenerating}
          aria-busy={isGenerating}
          aria-label={
            isGenerating ? t('report.iaUnavailableGenerating') : t('report.iaUnavailableCta')
          }
        >
          {isGenerating ? (
            <>
              <span className="report-generate-btn-spinner" aria-hidden="true" />
              {t('report.iaUnavailableGenerating')}
            </>
          ) : (
            t('report.iaUnavailableCta')
          )}
        </button>

        {retryState.kind === 'error' ? (
          <p className="report-ia-error-message" role="alert">
            <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
              error_outline
            </span>
            {t('report.iaUnavailableErrorMsg')}{' '}
            <a href="mailto:suporte@mktvibe.com.br" className="report-action-link">
              {t('report.iaUnavailableSupport')}
            </a>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/* ================================================================
   SKELETON DO DETALHE
   ================================================================ */

function ReportDetailSkeleton(): JSX.Element {
  return (
    <div
      className="report-detail-loading"
      role="status"
      aria-label={t('report.generatingAria')}
      aria-busy="true"
    >
      {/* Abertura */}
      <div className="report-detail-skeleton-opening">
        <Skeleton variant="text" width="120px" />
        <Skeleton variant="heading" width="80%" />
        <Skeleton variant="text" width="95%" />
        <Skeleton variant="text" width="85%" />
      </div>
      {/* O que mudou — 4 linhas */}
      <div className="report-detail-skeleton-block">
        <Skeleton variant="text" width="100px" />
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="75%" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="65%" />
      </div>
      {/* Por que importa — 3 paragrafos */}
      <div className="report-detail-skeleton-block">
        <Skeleton variant="text" width="110px" />
        <Skeleton variant="text" width="95%" />
        <Skeleton variant="text" width="88%" />
        <Skeleton variant="text" width="72%" />
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="60%" />
      </div>
    </div>
  );
}

/* ================================================================
   PAGINA PRINCIPAL
   ================================================================ */

export function ReportDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [iaRetry, setIaRetry] = useState<IaRetryState>({ kind: 'idle' });

  const load = useCallback(async (): Promise<void> => {
    if (id === undefined || id.length === 0) {
      setState({ kind: 'not-found' });
      return;
    }

    setState({ kind: 'loading' });
    try {
      const data = await apiClient.get<ReportDetail>(`/reports/${id}`);
      setState({ kind: 'ready', data });
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 404) {
        setState({ kind: 'not-found' });
      } else {
        setState({ kind: 'error' });
      }
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Dispara nova tentativa de geracao da IA (estado ia-unavailable). */
  const handleIaRetry = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    setIaRetry({ kind: 'generating' });
    try {
      await apiClient.post<GenerateResponse>('/reports/generate', {
        report_id: id,
        retry_analysis: true,
      });
      // Recarrega o relatorio para pegar os blocos 3 e 4 atualizados
      await load();
      setIaRetry({ kind: 'idle' });
    } catch {
      setIaRetry({ kind: 'error', message: t('report.iaUnavailableErrorMsg') });
    }
  }, [id, load]);

  // --- Dados derivados ---
  const report = state.kind === 'ready' ? state.data : null;
  const isMonthly = report?.type === 'monthly';
  const typeLabel = isMonthly ? t('report.badgeMonthly') : t('report.badgeWeekly');

  const sourcesCount = report?.blocks.sources.length ?? 0;
  const sourcesPlural = sourcesCount === 1 ? '' : 's';

  return (
    <section className="report-detail-page" aria-labelledby="report-detail-heading">
      {/* Back link */}
      <Link to="/reports" className="report-detail-back" aria-label={t('report.backToList')}>
        <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
          arrow_back
        </span>
        {t('report.backToList')}
      </Link>

      {/* Header sticky — apenas no estado ready */}
      {report !== null ? (
        <header className="report-detail-header" aria-label="Cabecalho do relatorio">
          <p className="report-detail-eyebrow">
            {t('report.pageEyebrow', {
              tipo: typeLabel,
              periodo: report.period_label ?? report.period_from,
            })}
          </p>
          <h1 id="report-detail-heading" className="report-detail-title">
            {/* Usa period_label como titulo; fallback para o template */}
            {report.period_label ??
              t('report.pageTitleFallback', { periodo: formatDate(report.period_from) })}
          </h1>
          <div className="report-detail-meta">
            <span className="report-type-badge">{typeLabel}</span>
            <span>{t('report.generatedAt', { data: formatDateTime(report.generated_at) })}</span>
            <span>
              {t('report.sourcesUsed', {
                n: String(sourcesCount),
                s: sourcesPlural,
              })}
            </span>
          </div>
        </header>
      ) : (
        /* Heading acessivel para estados sem dados */
        <h1
          id="report-detail-heading"
          className="report-detail-title"
          style={{ marginBottom: 'var(--mkv-space-6)' }}
        >
          {state.kind === 'not-found' ? t('report.notFoundTitle') : ''}
        </h1>
      )}

      {/* Estado: loading */}
      {state.kind === 'loading' ? <ReportDetailSkeleton /> : null}

      {/* Estado: not-found */}
      {state.kind === 'not-found' ? (
        <div className="report-detail-error" role="alert">
          <span
            className="material-symbols-rounded"
            aria-hidden="true"
            style={{ fontSize: 40, color: 'var(--md-on-surface-variant)' }}
          >
            find_in_page
          </span>
          <p className="report-detail-error-body">{t('report.notFoundBody')}</p>
          <div className="report-detail-error-actions">
            <Link to="/reports" className="report-btn-primary">
              {t('report.notFoundCta')}
            </Link>
          </div>
        </div>
      ) : null}

      {/* Estado: error */}
      {state.kind === 'error' ? (
        <div className="report-detail-error" role="alert">
          <span
            className="material-symbols-rounded"
            aria-hidden="true"
            style={{ fontSize: 40, color: 'var(--md-error)' }}
          >
            error_outline
          </span>
          <h2 className="report-detail-error-title">{t('report.errorTitle')}</h2>
          <p className="report-detail-error-body">{t('report.errorBody')}</p>
          <div className="report-detail-error-actions">
            <button type="button" className="report-btn-primary" onClick={() => void load()}>
              {t('report.errorCta')}
            </button>
            <a href="mailto:suporte@mktvibe.com.br" className="report-btn-secondary">
              {t('report.errorCta2')}
            </a>
          </div>
        </div>
      ) : null}

      {/* Estado: ready / ready-partial / ia-unavailable / ready-no-actions */}
      {state.kind === 'ready' ? (
        <div className="report-blocks">
          {/* BLOCO 1 — sempre presente */}
          <Block1Opening opening={state.data.blocks.opening} isMonthly={isMonthly ?? false} />

          {/* BLOCO 2 — sempre presente */}
          {state.data.blocks.changes.items.length > 0 ? (
            <Block2Changes changes={state.data.blocks.changes} />
          ) : null}

          {/* BLOCO 3 + 4 — condicionais: presentes se ai_status=ready; nota se ia-unavailable/fallback */}
          {state.data.ai_status === 'ready' && state.data.blocks.analysis.text !== null ? (
            <>
              <Block3Analysis analysis={state.data.blocks.analysis} reportId={state.data.id} />
              <Block4Actions actions={state.data.blocks.actions} reportId={state.data.id} />
            </>
          ) : (
            /* ia-unavailable ou fallback: banner + botao retry */
            <div>
              <IaUnavailableBanner retryState={iaRetry} onRetry={() => void handleIaRetry()} />
            </div>
          )}

          {/* BLOCO 5 — sempre presente */}
          <Block5Sources sources={state.data.blocks.sources} />
        </div>
      ) : null}

      {/* Botao "Gerar novo relatorio" — disponivel na lista, aqui aparece como acao secundaria */}
      {state.kind === 'ready' ? (
        <div style={{ marginTop: 'var(--mkv-space-8)', textAlign: 'center' }}>
          <button
            type="button"
            className="report-btn-secondary"
            onClick={() => navigate('/reports')}
          >
            <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
              list
            </span>
            {t('report.backToList')}
          </button>
        </div>
      ) : null}
    </section>
  );
}
